import { type AccentHue, colorize } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import type { IllustrationName } from "@repo/fika-design-system/illustrations"
import type { ParentProps } from "solid-js"
import { Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import { Illustration } from "@/designSystem/components/illustration"
import { Text } from "@/designSystem/components/text"
import {
  bulletDescriptionClass,
  bulletIconRecipe,
  explanationContentClass,
  explanationIllustrationClass,
  explanationRecipe,
} from "./explanation.css"

interface Props extends ParentProps {
  hue: AccentHue
  title: string
  illustration?: IllustrationName
}

function Explanation(props: Props) {
  return (
    <div class={explanationRecipe({ hue: props.hue })}>
      <div class={explanationContentClass}>
        <Text
          fontSize="h2"
          fontWeight="700"
          fontFamily="serif"
          color={colorize(props.hue, "900")}
        >
          {props.title}
        </Text>
        <Box flexDirection="column" gap="16">
          {props.children}
        </Box>
      </div>
      <Show when={props.illustration}>
        {illustration => (
          <div class={explanationIllustrationClass}>
            <Illustration name={illustration()} size="200" />
          </div>
        )}
      </Show>
    </div>
  )
}

interface BulletProps extends ParentProps {
  hue: AccentHue
  iconName: IconName
  title: string
  description?: string
}

function Bullet(props: BulletProps) {
  return (
    <Box flexDirection="row" gap="8">
      <div class={bulletIconRecipe({ hue: props.hue })}>
        <Icon
          name={props.iconName}
          size="18"
          color={colorize(props.hue, "700")}
        />
      </div>
      <Box flexDirection="column">
        <Text
          fontSize="readable"
          fontWeight="600"
          fontFamily="serif"
          color={colorize(props.hue, "900")}
        >
          {props.title}
        </Text>
        <Show when={props.description}>
          {description => (
            <Text
              fontSize="m"
              color={colorize(props.hue, "900")}
              class={bulletDescriptionClass}
            >
              {description()}
            </Text>
          )}
        </Show>
        {props.children}
      </Box>
    </Box>
  )
}

Explanation.Bullet = Bullet

export { Explanation }
